/**
 * useImpersonateState — l'échange du code « Impersonner » du back-office (`/impersonate?code=…`) :
 * une fois, après `resetAuthState` (rien de la session d'avant ne survit), puis l'accueil du compte
 * vu ; un code refusé reste sur la page, qui dit la suite. Parité : `ports/TRANSITION.md`.
 *
 * Écrit à la main plutôt que par `useApiMutation`, pour une garde de MOINS : la réponse est
 * consommée sans vérifier que c'est cette instance qui a lancé l'appel. Le succès ouvre la session,
 * la pile change, et l'écran — posé dans toutes les piles — ne doit pas manquer sa réponse : le
 * legacy (`useApiStatus`) la consommait de la même façon. Pour la même raison, la tranche n'est pas
 * rendue au repos au démontage.
 */
import { useEffect, useRef, useState } from "react"
import { useNavigation } from "@react-navigation/native"
import { useDispatch, useSelector } from "react-redux"

import { apiIdle, apiRequest } from "../../../../../../data/redux/actions/api"
import { resetAuthState } from "../../../../../../data/redux/actions/authTokens"
import { setIsParentMode } from "../../../../../../data/redux/actions/parentMode"
import { authService } from "../../../../../../data/services/authService"
import { impersonateLanding } from "../../../../view-models/auth/Impersonate/impersonateViewModel"

type BootstrapSlice = { success?: boolean; error?: unknown; data?: { user?: { roles?: string[] } } }

type ImpersonateNavigation = { navigate: (name: string, params?: Record<string, unknown>) => void }

export function useImpersonateState(code: string | undefined) {
    const navigation = useNavigation<ImpersonateNavigation>()
    const dispatch = useDispatch()
    const [failed, setFailed] = useState(false)

    const req = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => state?.api?.bootstrapImpersonation as BootstrapSlice | undefined
    )

    const submittedRef = useRef(false)
    useEffect(() => {
        if (!code || submittedRef.current) return
        submittedRef.current = true
        dispatch(resetAuthState() as never)
        dispatch(apiRequest(authService.bootstrapImpersonation, {}, { code }) as never)
    }, [code, dispatch])

    const handledSuccessRef = useRef(false)
    useEffect(() => {
        if (!req?.success || handledSuccessRef.current) return
        handledSuccessRef.current = true
        dispatch(apiIdle(authService.bootstrapImpersonation) as never)
        // `setIsParentMode(true)` bascule la pile parent sur une session neuve, mais ne fait rien
        // depuis une session parent déjà ouverte : d'où la navigation explicite dans les deux cas.
        const landing = impersonateLanding(req.data?.user)
        dispatch(setIsParentMode(landing.parentMode) as never)
        navigation.navigate("menu", landing.screen ? { screen: landing.screen } : undefined)
    }, [dispatch, navigation, req?.success, req?.data])

    const handledErrorRef = useRef(false)
    useEffect(() => {
        if (!req?.error || handledErrorRef.current) return
        handledErrorRef.current = true
        setFailed(true)
        dispatch(apiIdle(authService.bootstrapImpersonation) as never)
    }, [dispatch, req?.error])

    return { failed }
}
